import React, { useState } from 'react';

// NJ 2024 Income Tax Brackets
const NJ_INCOME_TAX_BRACKETS = [
    { max: 20000, rate: 0.014, base: 0 },
    { max: 35000, rate: 0.0175, base: 280 },
    { max: 40000, rate: 0.035, base: 542.50 },
    { max: 75000, rate: 0.05525, base: 717.50 },
    { max: 500000, rate: 0.0637, base: 2651.25 },
    { max: 1000000, rate: 0.0897, base: 29723.75 },
    { max: Infinity, rate: 0.1075, base: 74573.75 }
];

function IncomeTaxCalculator() {
    const [income, setIncome] = useState('');
    const [filingStatus, setFilingStatus] = useState('single');
    const [result, setResult] = useState(null);

    const calculateTax = (e) => {
        e.preventDefault();
        const numIncome = parseFloat(income);

        if (isNaN(numIncome) || numIncome <= 0) return;

        // Calculate NJ Income Tax based on brackets
        let tax = 0;
        let bracket = NJ_INCOME_TAX_BRACKETS.find(b => numIncome <= b.max);
        
        if (bracket) {
            // For simple calculation, we'll use base + rate*(income - previous bracket max)
            const prevBracket = NJ_INCOME_TAX_BRACKETS[NJ_INCOME_TAX_BRACKETS.indexOf(bracket) - 1];
            const bracketMin = prevBracket ? prevBracket.max : 0;
            tax = bracket.base + bracket.rate * (numIncome - bracketMin);
        }

        // Apply filing status adjustments (simplified)
        if (filingStatus === 'married') {
            tax *= 0.9; // 10% reduction for married filing jointly (simplified)
        } else if (filingStatus === 'head') {
            tax *= 0.95; // 5% reduction for head of household (simplified)
        }

        setResult({
            income: numIncome,
            tax: tax,
            netIncome: numIncome - tax,
            effectiveRate: (tax / numIncome) * 100
        });
    };

    return (
        <div>
            <div className="calculator-header">
                <h3>Income Tax Estimator</h3>
                <p className="disclaimer">This is a simplified estimate for NJ state income tax only.</p>
            </div>

            <form onSubmit={calculateTax}>
                <div className="input-group">
                    <label>Annual Income:</label>
                    <div className="input-with-icon">
                        <span className="currency-symbol">$</span>
                        <input
                            type="number"
                            step="1000"
                            value={income}
                            onChange={(e) => setIncome(e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                </div>

                <div className="input-group">
                    <label>Filing Status:</label>
                    <select 
                        value={filingStatus} 
                        onChange={(e) => setFilingStatus(e.target.value)}
                        className="select-input"
                    >
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                        <option value="separate">Married Filing Separately</option>
                        <option value="head">Head of Household</option>
                    </select>
                </div>

                <button type="submit" className="calculate-btn">Estimate Income Tax</button>
            </form>

            {result && (
                <div className="results">
                    <div className="result-item">
                        <span className="result-label">Annual Income:</span>
                        <span className="result-value">${result.income.toLocaleString()}</span>
                    </div>
                    <div className="result-item">
                        <span className="result-label">Estimated NJ Income Tax:</span>
                        <span className="result-value">${result.tax.toFixed(2)}</span>
                    </div>
                    <div className="result-item">
                        <span className="result-label">Effective Tax Rate:</span>
                        <span className="result-value">{result.effectiveRate.toFixed(2)}%</span>
                    </div>
                    <div className="result-item total">
                        <span className="result-label">After-Tax Income:</span>
                        <span className="result-value">${result.netIncome.toFixed(2)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default IncomeTaxCalculator;
